import { useEffect, useRef, useState } from "react";
import type { ComponentProps, ReactElement, ReactNode } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Anhaenge as AttachmentsState, BelegParteien as PartiesState, PositionsStand as ItemsState,
              Zahlplan as PaymentPlanState } from "@kundeninfo/api-client";
import { Feld } from "@kundeninfo/ui";
import type { Objektart } from "@kundeninfo/ui/me-design";
import { PlatzhalterTextfeld } from "../PositionsEditor.js";
import { Vorgangsabschnitt, ersteZeile } from "../Vorgangsabschnitt.js";
import { Vorgangsdateien } from "../Vorgangsdateien.js";
import type { VorgangsdateienProps } from "../Vorgangsdateien.js";
import { PAYMENT_PLAN_FIELD, ZAHLPLAN_KOPFFELDER, istZahlplanFeld } from "../Zahlplan.js";
import { formatBetrag } from "../../format.js";
import { sectionOfField, errorsPerSection, showFirstErrorIn, showFieldIn } from "./sectionFolds.js";
import { SECTION_TEXT } from "./texts.js";
import type { SectionFields, SectionFolds } from "./sectionFolds.js";
import type { Kopffaltung } from "../Kopffaltung.js";

/*
 * Was Angebots- und Auftragsmaske an ihren Abschnitten TEILEN (Hausregel „Code kompakt“): das Zeichnen eines Abschnitts
 * aus Schlüssel, Objektart, Titel und Inhalt, den Weg eines Feldbefundes in seinen Abschnitt und den geladenen Stand,
 * aus dem zugeklappte Köpfe ihre Zahl lesen. Ratenbefunde tragen einen Index (`raten[2].sharePercent`) und stehen in
 * keiner festen Tabelle — sie gehören zu dem Abschnitt, der `PAYMENT_PLAN_FIELD` führt (`ZAHLPLAN_KOPFFELDER`).
 */

/** Der Inhalt eines Abschnitts aus einem Schlitz und was sein Kopf zugeklappt sagt. */
export type SectionContent = {
  inhalt: ReactNode;
  zusammenfassung?: string | undefined;
  zaehler?: number | undefined;
  /** Bedienelemente rechts im Kopf (Slot `aktionen`). */
  aktionen?: ReactNode;
};

/** Die Props `zusammenfassung`/`zaehler` eines Abschnitts — ohne Wert weggelassen (`exactOptionalPropertyTypes`). */
export const summaryProp = (text: string | undefined): { zusammenfassung?: string } =>
  (text === undefined ? {} : { zusammenfassung: text });
export const counterProp = (count: number | null | undefined): { zaehler?: number } =>
  (count === null || count === undefined ? {} : { zaehler: count });

/** Die Props eines Textabschnitts (Einleitungs-/Ausleitungstext): das Textfeld mit Platzhalterhilfe; Titel und Wachsen setzt
 *  der Abschnitt. */
export type TextFieldProps = Omit<ComponentProps<typeof PlatzhalterTextfeld>, "label" | "wachsend">;

/** Die Props der Notiz: ein schlichtes mehrzeiliges Feld ohne Platzhalter (sie geht in keinen Beleg). */
export type NoteFieldProps = { id: string; wert: string; onChange: (wert: string) => void };

/** Der Kopf der Positionen: Zähler der Positionszeilen (ohne Text- und Zwischensummenzeilen) und die Nettosumme;
 *  solange der Editor nichts gemeldet hat, nichts. Einmal hier für Angebot, Auftrag und Rechnung. */
export function itemsHead(state: ItemsState | null, currency: string): { zaehler?: number; zusammenfassung?: string } {
  return state === null ? {}
    : { zaehler: state.items.filter((z) => z.kind === "ITEM").length, zusammenfassung: formatBetrag(state.netAmount, currency) };
}

/** Text da? Die Textabschnitte beginnen dann offen (Spec-Tabelle: „offen, wenn Text da ist“). */
export const hasText = (text: string): boolean => text.trim() !== "";

/** Der Abschnitt eines Serverfeldes; ein Ratenbefund mit Index gehört zur Zahlungsvereinbarung. */
export function sectionOf<K extends string>(tables: SectionFields<K>, field: string): K | null {
  return sectionOfField(tables, istZahlplanFeld(field) ? PAYMENT_PLAN_FIELD : field);
}

/**
 * Den Abschnitt des ERSTEN Befundes eines Serverfehlers öffnen und sein Feld fokussieren. Ein Ratenbefund zielt auf die
 * Zahlungsvereinbarung als Ganzes; ein Fehler ohne Befunde (Netzabbruch) lässt alles zu.
 */
export function showFindingIn<K extends string>(
  f: SectionFolds<K>, tables: SectionFields<K>, prefix: string, error: unknown,
): void {
  if (error instanceof ApiError && (error.fields ?? []).some((b) => istZahlplanFeld(b.field))) {
    showFieldIn(f, tables, prefix, PAYMENT_PLAN_FIELD);
    return;
  }
  showFirstErrorIn(f, tables, prefix, error);
}

/**
 * Jeden Abschnitt öffnen, in dem ein 422-Befund des Servers steht — ohne Fokuswunsch: Den Fokus setzt
 * `me-formular.zumErstenFehler()` nach dem Zeichnen, und der findet das Feld nur, wenn sein Abschnitt im SELBEN Zug offen
 * ist.
 */
export function openSectionsWithFindings<K extends string>(
  f: SectionFolds<K>, tables: SectionFields<K>, error: unknown,
): void {
  if (!(error instanceof ApiError) || error.status !== 422) return;
  for (const finding of error.fields ?? []) {
    const k = sectionOf(tables, finding.field);
    if (k !== null) f[k].oeffnen();
  }
}

/** Wie viele Felder je Abschnitt einen Befund tragen; Ratenbefunde mit Index zählen an der Zahlungsvereinbarung. */
export function findingsPerSection<K extends string>(
  tables: SectionFields<K>, findings: Readonly<Record<string, string>> | undefined,
): Record<K, number> {
  const count = errorsPerSection(tables, findings);
  const k = sectionOfField(tables, PAYMENT_PLAN_FIELD);
  if (k !== null) {
    count[k] += Object.keys(findings ?? {})
      .filter((field) => istZahlplanFeld(field) && ZAHLPLAN_KOPFFELDER[field] === undefined).length;
  }
  return count;
}

/**
 * Die Zeichner der Abschnitte einer Maske: `section` zeichnet einen Abschnitt, `slotSection` einen aus einem Schlitz,
 * den es beim Anlegen nicht gibt, `textSection` einen Textabschnitt (Objektart Notiz, Text wächst mit, zugeklappt die erste
 * Zeile), `noteSection` die Notiz ebenso, nur ohne Platzhalter und Vorschau. Die Kennung des Inhalts folgt dem Präfix; die
 * Kopfdaten (Schlüssel `head` in jeder Maske) behalten `-kopfdaten`.
 */
export function sectionRenderer<K extends string>(
  faltungen: SectionFolds<K>, prefix: string, errors: Readonly<Record<K, number>>,
): {
  section: (k: K, art: Objektart, title: string, content: SectionContent) => ReactElement;
  slotSection: (k: K, art: Objektart, title: string, content: SectionContent | undefined) => ReactElement | null;
  textSection: (k: K, title: string, field: TextFieldProps) => ReactElement;
  noteSection: (k: K, title: string, field: NoteFieldProps) => ReactElement;
} {
  function section(k: K, art: Objektart, title: string, content: SectionContent): ReactElement {
    return (
      <Vorgangsabschnitt key={k} art={art} titel={title} faltung={faltungen[k]}
        inhaltId={k === "head" ? `${prefix}-kopfdaten` : `${prefix}-abschnitt-${k}`} fehler={errors[k]}
        {...summaryProp(content.zusammenfassung)} {...counterProp(content.zaehler)}
        aktionen={content.aktionen}>
        {content.inhalt}
      </Vorgangsabschnitt>
    );
  }
  return {
    section,
    textSection: (k, title, field) => section(k, "notiz", title, {
      zusammenfassung: ersteZeile(field.wert),
      inhalt: <PlatzhalterTextfeld {...field} label={title} wachsend />,
    }),
    noteSection: (k, title, field) => section(k, "notiz", title, {
      zusammenfassung: ersteZeile(field.wert),
      inhalt: <Feld {...field} label={title} typ="mehrzeilig" zeilen={3} wachsend bearbeitbar />,
    }),
    slotSection: (k, art, title, content) => (content === undefined ? null : section(k, art, title, content)),
  };
}

/** Was eine Maske dem Abschnitt „Dateien“ reicht; wann er lädt und wohin er die Zahl meldet, setzt `filesContent`. */
export type FilesSource = Omit<VorgangsdateienProps, "onZahl">;

/**
 * Der Inhalt des Abschnitts „Dateien“ (Spec-Tabelle: Objektart `datei`, Start zu, zugeklappt die Zahl der Dateien) —
 * EINMAL hier für Angebot, Auftrag und Rechnung, im Bearbeiten-Panel wie im geschlossenen Detail. `load`: im Panel
 * sofort (zugeklappt steht die Zahl wie bei den Anlagen), im geschlossenen Detail erst mit dem Aufklappen (lazy, kein
 * Abruf je Listenzeile).
 */
export function filesContent(source: FilesSource, load: boolean,
  state: { fileCount: number | null; onFilesCounted: (n: number) => void }): SectionContent {
  return {
    ...counterProp(state.fileCount),
    // Erst eingehängt, wenn er laden soll — zu und nie geöffnet steht kein „Lädt …“ im (versteckten) Abschnitt.
    inhalt: load && <Vorgangsdateien {...source} onZahl={state.onFilesCounted} />,
  };
}

/**
 * Der Sprung „Ordner“ aus dem Abschnitt „Dateien“ im BEARBEITEN-Panel (Fix-Runde 1 zu Aufgabe 25): Er ist ein Ausgang aus
 * dem strikten Modalpanel und geht deshalb durch dessen einen Ausgang (`leave`, fragt bei Ungespeichertem, setzt zurück,
 * schließt). Gesprungen wird erst, wenn das Panel zu ist und die Rahmen ihre Verlassen-Wächter abgeräumt haben — sonst
 * fragte der Bereich ein zweites Mal. Der Wächter hängt am Panelzustand des Rahmens (am Rechnungsbereich über
 * `onBearbeitungWechsel` einen Zug später); Effekte räumen in jedem Zug ab, bevor neue laufen — darum springt der
 * Effekt erst im zweiten Zug nach dem Schließen.
 */
export function useJumpAfterLeaving<T>(open: boolean, leave: () => Promise<boolean>, jump: (target: T) => void):
    (target: T) => void {
  // Generisch (P7): das Ziel ist heute ein Ordner, der Weg taugt für jedes Ziel, das erst nach dem Schließen gilt.
  const [pending, setPending] = useState<{ target: T; settled: boolean } | null>(null);
  const jumpRef = useRef(jump);
  jumpRef.current = jump;
  useEffect(() => {
    if (open || pending === null) return;
    if (!pending.settled) { setPending({ ...pending, settled: true }); return; }
    setPending(null);
    jumpRef.current(pending.target);
  }, [open, pending]);
  return (target) => { void leave().then((ok) => { if (ok) setPending({ target, settled: false }); }); };
}

/** Der Abschnitt „Dateien“ im geschlossenen Detail (lesende Abschnitte zeichnen die Masken einzeln). */
export function FilesReadSection(p: { faltung: Kopffaltung; inhaltId: string; content: SectionContent }): ReactElement {
  return (
    <Vorgangsabschnitt art="datei" titel={SECTION_TEXT.files} faltung={p.faltung} inhaltId={p.inhaltId}
      {...counterProp(p.content.zaehler)}>
      {p.content.inhalt}
    </Vorgangsabschnitt>
  );
}

/**
 * Der GELADENE Stand, aus dem zugeklappte Köpfe ihre Aussage lesen: Parteien, Zahl der Raten, der Anlagen und der Dateien. Die
 * Bausteine melden ihn über `onLoaded` (die Anlagen nach jedem Laden und Schreiben); `undefined`/`null` heißt „noch
 * nicht geladen“, der Kopf bleibt dann ohne Zahl.
 *
 * Panel und geschlossenes Detail derselben Instanz teilen ihn. Darum gilt er nur für den Ort, an dem er geladen wurde:
 * Die Maske ruft `reset()` bei JEDEM Wechsel zwischen Panel und Detail. Sonst stünde nach dem Speichern im Panel im
 * Detail die Zahl von vorher (Fixrunde 1 zu Aufgabe 9) — die Bausteine dort laden erst beim Aufklappen neu.
 */
export function useLoadedState(): {
  parties: PartiesState | null; rateCount: number | null; attachmentCount: number | null; fileCount: number | null;
  representativeCount: number | null;
  onRepresentativesLoaded: (state: { kategorien: readonly unknown[] }) => void;
  onPartiesLoaded: (state: PartiesState) => void;
  onRatesLoaded: (state: PaymentPlanState) => void;
  onAttachmentsLoaded: (state: AttachmentsState) => void;
  onFilesCounted: (count: number) => void;
  reset: () => void;
} {
  const [parties, setParties] = useState<PartiesState | null>(null);
  const [rateCount, setRateCount] = useState<number | null>(null);
  const [attachmentCount, setAttachmentCount] = useState<number | null>(null);
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [representativeCount, setRepresentativeCount] = useState<number | null>(null);
  return {
    parties, rateCount, attachmentCount, fileCount, representativeCount,
    onRepresentativesLoaded: (state) => setRepresentativeCount(state.kategorien.length),
    onPartiesLoaded: setParties,
    onRatesLoaded: (state) => setRateCount(state.raten.length),
    onAttachmentsLoaded: (state) => setAttachmentCount(state.anhaenge.length),
    onFilesCounted: setFileCount,
    reset: () => {
      setParties(null); setRateCount(null); setAttachmentCount(null); setFileCount(null); setRepresentativeCount(null);
    },
  };
}
